import { createSelector } from 'reselect';

import { selectTargetPlatform } from '@pressreader/appcore';
import { selectGetConfigValue } from '@pressreader/config';
import { NavigationConfig, ViewName } from '@pressreader/navigation-types';
import { TargetPlatform } from '@pressreader/types';

import { INavigationConfigDto } from '../api/types';
import { NAVIGATION_CORE_MODULE_NAME } from '../constants';
import { isNavConfigWithArticlePopupId } from '../utils';

import { IState } from './reducer';

/** The lib's own slice of the root state — the base of the selectors here and in `./selectors.views`. */
export const selectNavigationCoreState = (state: Record<string, unknown>) => state[NAVIGATION_CORE_MODULE_NAME] as IState;

export const selectHandleRouting = createSelector(selectNavigationCoreState, state => state.handleRouting);

export const selectNavigationConfig = createSelector(selectGetConfigValue, selectTargetPlatform, (getConfigValue, platform): NavigationConfig => {
    const configDto = getConfigValue<INavigationConfigDto>('app.common.navigation');

    const featureFlagName = platform === TargetPlatform.Pervasive ? 'features.pervasive.home.view.name' : 'features.home.view.name';
    const homeViewName = getConfigValue<ViewName>(featureFlagName);

    if (!configDto || !configDto.landing_url_feature_key) {
        return { landingUrl: null, homeViewName };
    }

    return {
        landingUrl: getConfigValue<string | null>(configDto.landing_url_feature_key, null),
        homeViewName,
    };
});

export const selectViewName = createSelector(selectNavigationCoreState, state => state.viewName);
export const selectSubviewName = createSelector(selectNavigationCoreState, state => state.subviewName);
export const selectPrevNavConfig = createSelector(selectNavigationCoreState, state => state.prevNavConfig);
export const selectNavConfig = createSelector(selectNavigationCoreState, state => state.navConfig);

export const selectIsArticlePopupRouteOpen = createSelector(
    selectNavConfig,
    currentViewNavConfig => !!currentViewNavConfig && isNavConfigWithArticlePopupId(currentViewNavConfig),
);

export const selectLocation = createSelector(selectNavigationCoreState, state => state.location);
export const selectNavigationOrigin = createSelector(selectNavigationCoreState, state => state.navigationOrigin);
export const selectLocationState = createSelector(selectLocation, location => location.state);
export const selectNavigationHistoryState = createSelector(selectLocationState, selectViewName, (state, viewName) => ({
    state,
    stateLess: {},
    viewName: viewName === null ? ViewName.Error : viewName,
}));

export const selectNavConfigSuppressRefocus = createSelector(selectNavConfig, navConfig => navConfig?.suppressRefocus);

export const selectRefocusTargetActive = createSelector(selectNavigationCoreState, state => state.refocusTargetActive);

export const selectIsNavigationInProgress = createSelector(selectNavigationCoreState, state => state.isNavigationInProgress);

export const selectCurrentNavId = createSelector(selectNavigationCoreState, state => state.currentNavId);
